/* ============================================================
   TRIAL & FLORA — botanical plate engine
   Deterministic SVG "botanical plates" per plant, drawn in the
   brand palette sampled from the logo. No stock photos — every
   listing gets its own engraving-style illustration.
   ============================================================ */

const TF_ART = (() => {
  let UID = 0;
  const uid = () => "tfa" + (UID++).toString(36);

  /* deterministic prng */
  function hashCode(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }
  function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  const C = {
    ink: "#2c4a2c", forest: "#294e29", dark: "#22381f",
    leaf: "#6f8f4f", leafLight: "#9db36a", moss: "#4e7038",
    cream: "#efe9c8", chart: "#ece5a8", paper: "#f8f3e6",
    gold: "#b5924b", goldL: "#d4b36a",
    blush: "#e8a9ad", blushD: "#c8848a", pink: "#e7a6b8",
    wine: "#6e2432", silver: "#c3d2bd", cebu: "#7fa08e",
    caudex: "#d8c79e", caudexD: "#8a7146", bronze: "#59512e",
    velvet: "#3c5a34", deep: "#2e4027",
  };

  const N = (v) => (Math.round(v * 10) / 10);

  /* ---------- geometry helpers ---------- */

  function cubicAt(p0, p1, p2, p3, t) {
    const u = 1 - t;
    return {
      x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
      y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
    };
  }
  function cubicTan(p0, p1, p2, p3, t) {
    const u = 1 - t;
    const x = 3 * u * u * (p1.x - p0.x) + 6 * u * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x);
    const y = 3 * u * u * (p1.y - p0.y) + 6 * u * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y);
    return (Math.atan2(y, x) * 180) / Math.PI;
  }
  const cubicPath = (p0, p1, p2, p3) =>
    `M${N(p0.x)} ${N(p0.y)} C ${N(p1.x)} ${N(p1.y)}, ${N(p2.x)} ${N(p2.y)}, ${N(p3.x)} ${N(p3.y)}`;

  function ovalLeafPath(L, W) {
    return `M0 0 C ${N(W * 0.54)} ${N(-L * 0.2)}, ${N(W * 0.58)} ${N(-L * 0.64)}, 0 ${N(-L)} C ${N(-W * 0.58)} ${N(-L * 0.64)}, ${N(-W * 0.54)} ${N(-L * 0.2)}, 0 0 Z`;
  }
  function heartLeafPath(L, W) {
    return `M0 ${N(L)} C ${N(-W * 0.64)} ${N(L * 0.6)}, ${N(-W * 0.74)} ${N(L * 0.16)}, ${N(-W * 0.52)} ${N(-L * 0.04)} C ${N(-W * 0.36)} ${N(-L * 0.2)}, ${N(-W * 0.12)} ${N(-L * 0.18)}, 0 ${N(-L * 0.02)} C ${N(W * 0.12)} ${N(-L * 0.18)}, ${N(W * 0.36)} ${N(-L * 0.2)}, ${N(W * 0.52)} ${N(-L * 0.04)} C ${N(W * 0.74)} ${N(L * 0.16)}, ${N(W * 0.64)} ${N(L * 0.6)}, 0 ${N(L)} Z`;
  }

  function blobPath(r, cx, cy, rad, n = 6, irr = 0.42) {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.5;
      const rr = rad * (1 - irr / 2 + r() * irr);
      pts.push({ x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr });
    }
    let d = `M ${N((pts[0].x + pts[1].x) / 2)} ${N((pts[0].y + pts[1].y) / 2)}`;
    for (let i = 1; i <= n; i++) {
      const p = pts[i % n], m = pts[(i + 1) % n];
      d += ` Q ${N(p.x)} ${N(p.y)} ${N((p.x + m.x) / 2)} ${N((p.y + m.y) / 2)}`;
    }
    return d + " Z";
  }

  function starPath(cx, cy, R, r2, rot = 0, n = 5) {
    let d = "";
    for (let i = 0; i < n * 2; i++) {
      const rad = i % 2 ? r2 : R;
      const a = rot + (i * Math.PI) / n - Math.PI / 2;
      d += (i ? "L" : "M") + N(cx + Math.cos(a) * rad) + " " + N(cy + Math.sin(a) * rad) + " ";
    }
    return d + "Z";
  }

  /* ---------- botanical parts ---------- */

  function hoyaFlower(cx, cy, s, rot, cols) {
    const o = cols || { outer: C.blush, inner: C.cream, dot: C.wine };
    return (
      `<path d="${starPath(cx, cy, 13 * s, 6.6 * s, rot)}" fill="${o.outer}" stroke="${C.wine}" stroke-width="${N(0.7 * s)}" stroke-opacity="0.55"/>` +
      `<path d="${starPath(cx, cy, 6.4 * s, 3 * s, rot + Math.PI / 5)}" fill="${o.inner}" stroke="${C.wine}" stroke-width="${N(0.5 * s)}" stroke-opacity="0.4"/>` +
      `<circle cx="${N(cx)}" cy="${N(cy)}" r="${N(1.7 * s)}" fill="${o.dot}"/>`
    );
  }

  function umbel(r, cx, cy, scale = 1, cols) {
    let s = "";
    const nRing = 9;
    // spokes first
    for (let i = 0; i < nRing; i++) {
      const a = (i / nRing) * Math.PI * 2 + 0.2;
      const fx = cx + Math.cos(a) * 27 * scale, fy = cy + Math.sin(a) * 25 * scale;
      s += `<line x1="${N(cx)}" y1="${N(cy)}" x2="${N(fx)}" y2="${N(fy)}" stroke="${C.wine}" stroke-width="${N(0.9 * scale)}" stroke-opacity="0.5"/>`;
    }
    for (let i = 0; i < nRing; i++) {
      const a = (i / nRing) * Math.PI * 2 + 0.2;
      const fx = cx + Math.cos(a) * 27 * scale, fy = cy + Math.sin(a) * 25 * scale;
      s += hoyaFlower(fx, fy, 0.78 * scale, r() * 1.2, cols);
    }
    for (let i = 0; i < 3; i++) {
      const a = r() * Math.PI * 2;
      s += hoyaFlower(cx + Math.cos(a) * 9 * scale, cy + Math.sin(a) * 8 * scale, 0.85 * scale, r() * 1.2, cols);
    }
    return s;
  }

  function midribOval(L, W, col) {
    return `<path d="M0 ${N(-L * 0.06)} C ${N(W * 0.05)} ${N(-L * 0.34)}, ${N(-W * 0.04)} ${N(-L * 0.6)}, 0 ${N(-L * 0.92)}" fill="none" stroke="${col}" stroke-width="1" stroke-opacity="0.65"/>`;
  }
  function midribHeart(L, W, col) {
    return `<path d="M0 ${N(L * 0.02)} C ${N(W * 0.05)} ${N(L * 0.3)}, ${N(-W * 0.04)} ${N(L * 0.62)}, 0 ${N(L * 0.94)}" fill="none" stroke="${col}" stroke-width="1.1" stroke-opacity="0.6"/>`;
  }

  /* one leaf, arbitrary shape + decoration */
  function leafG(r, { x, y, rot, L, W, shape, fill, stroke, deco, sw }) {
    const pathD = shape === "heart" ? heartLeafPath(L, W) : ovalLeafPath(L, W);
    const cid = uid();
    let inner = `<path d="${pathD}" fill="${fill}" stroke="${stroke}" stroke-width="${sw || 1.4}"/>`;
    let decoS = "";
    if (deco && deco.length) {
      decoS = `<clipPath id="${cid}"><path d="${pathD}"/></clipPath><g clip-path="url(#${cid})">`;
      for (const d of deco) decoS += d;
      decoS += `</g>`;
    }
    const rib = shape === "heart" ? midribHeart(L, W, stroke) : midribOval(L, W, stroke);
    return `<g transform="translate(${N(x)} ${N(y)}) rotate(${N(rot)})">${inner}${decoS}${rib}</g>`;
  }

  /* deco makers (coordinates local to leaf) */
  function decoFlecks(r, L, W, col, count, up) {
    let s = "";
    for (let i = 0; i < count; i++) {
      const yy = up ? -r() * L * 0.92 : (r() * 1.1 - 0.06) * L * 0.9;
      const xx = (r() - 0.5) * W * 0.85;
      const len = 2.5 + r() * 6, ang = r() * 180;
      s += `<line x1="${N(xx)}" y1="${N(yy)}" x2="${N(xx + Math.cos(ang) * len)}" y2="${N(yy + Math.sin(ang) * len)}" stroke="${col}" stroke-width="${N(1.4 + r() * 1.6)}" stroke-linecap="round" stroke-opacity="0.9"/>`;
    }
    return s;
  }
  function decoDots(r, L, W, col, count, up) {
    let s = "";
    for (let i = 0; i < count; i++) {
      const yy = up ? -r() * L * 0.9 : (r() * 1.1 - 0.05) * L * 0.88;
      const xx = (r() - 0.5) * W * 0.8;
      s += `<circle cx="${N(xx)}" cy="${N(yy)}" r="${N(1 + r() * 2)}" fill="${col}" fill-opacity="0.85"/>`;
    }
    return s;
  }
  function decoBlobs(r, L, W, col, count, up) {
    let s = "";
    for (let i = 0; i < count; i++) {
      const yy = up ? -(0.15 + r() * 0.7) * L : (0.05 + r() * 0.7) * L;
      const xx = (r() - 0.5) * W * 0.9;
      s += `<path d="${blobPath(r, xx, yy, (0.18 + r() * 0.2) * W, 6, 0.5)}" fill="${col}" fill-opacity="0.95"/>`;
    }
    return s;
  }
  function decoPinstripes(L, W, col, n, up) {
    let s = "";
    for (let i = 1; i <= n; i++) {
      const f = i / (n + 1);
      const xw = W * 0.56 * (1 - Math.abs(0.5 - f) * 0.4);
      const dir = up ? -1 : 1;
      s += `<path d="M0 ${N(dir * L * 0.04)} C ${N(xw * (f - 0.5) * 2.2)} ${N(dir * L * 0.3)}, ${N(xw * (f - 0.5) * 2.4)} ${N(dir * L * 0.55)}, ${N(xw * (f - 0.5) * 1.7)} ${N(dir * L * 0.82)}" fill="none" stroke="${col}" stroke-width="1.1" stroke-opacity="0.75"/>`;
    }
    return s;
  }
  function decoVeins(L, W, col, n, up) {
    /* radiating gloriosum-style veins for heart leaves (up=false: tip points +y) */
    let s = "";
    for (let i = 0; i < n; i++) {
      const f = (i + 1) / (n + 1);
      const side = i % 2 ? 1 : -1;
      s += `<path d="M0 ${N(L * 0.06)} C ${N(side * W * 0.24 * (1 + f))} ${N(L * (0.1 + f * 0.28))}, ${N(side * W * 0.34 * (1 + f * 0.6))} ${N(L * (0.24 + f * 0.4))}, ${N(side * W * 0.44)} ${N(L * (0.16 + f * 0.6))}" fill="none" stroke="${col}" stroke-width="2.2" stroke-opacity="0.85"/>`;
    }
    return s;
  }

  /* ---------- genus styles ---------- */

  const LEAF_STYLES = {
    /* returns {fill, stroke, deco(r,L,W,up), shape} */
    krimson:   () => ({ shape: "oval", fill: C.cream, stroke: C.forest, layer: { fill: C.leaf, scale: 0.66 } }),
    splash:    () => ({ shape: "oval", fill: C.leaf, stroke: C.forest, deco: (r, L, W, up) => decoDots(r, L, W, C.silver, 16, up) }),
    callisto:  () => ({ shape: "oval", fill: C.leafLight, stroke: C.dark, sw: 1.8, deco: (r, L, W, up) => decoVeinNet(L, W, C.dark, up) }),
    linearis:  () => ({ shape: "oval", fill: C.leaf, stroke: C.forest }),
    kerrii:    () => ({ shape: "heart", fill: C.leaf, stroke: C.forest, layer: { fill: C.cream, scale: 0.99, edgeOnly: true } }),
    bella:     () => ({ shape: "oval", fill: C.moss, stroke: C.dark }),
    hoyaPlain: () => ({ shape: "oval", fill: C.leaf, stroke: C.forest }),

    marble:    () => ({ shape: "heart", fill: C.leaf, stroke: C.forest, deco: (r, L, W, up) => decoFlecks(r, L, W, C.cream, 22, up) }),
    njoy:      () => ({ shape: "heart", fill: C.moss, stroke: C.forest, deco: (r, L, W, up) => decoBlobs(r, L, W, C.cream, 2, up) }),
    cebu:      () => ({ shape: "oval", fill: C.cebu, stroke: "#4e6b5c" }),
    teruno:    () => ({ shape: "heart", fill: C.leaf, stroke: C.forest, deco: (r, L, W, up) => decoBlobs(r, L, W, C.chart, 3, up) }),
    skeleton:  () => ({ shape: "heart", fill: C.moss, stroke: C.forest, deco: (r, L, W, up) => decoBlobs(r, L, W, C.chart, 2, up) }),
    global:    () => ({ shape: "heart", fill: C.dark, stroke: C.dark, deco: (r, L, W, up) => decoBlobs(r, L, W, C.leafLight, 2, up) }),

    gloriosum: () => ({ shape: "heart", fill: C.velvet, stroke: C.dark, deco: (r, L, W, up) => decoVeins(L, W, C.cream, 5, up) }),
    ppp:       () => ({ shape: "heart", fill: C.deep, stroke: C.dark, deco: (r, L, W, up) => decoBlobs(r, L, W, C.pink, 2, up) }),
    whiteknight: () => ({ shape: "heart", fill: C.deep, stroke: C.dark, deco: (r, L, W, up) => decoBlobs(r, L, W, C.cream, 2, up) }),
    birkin:    () => ({ shape: "oval", fill: C.velvet, stroke: C.dark, deco: (r, L, W, up) => decoPinstripes(L, W, C.cream, 7, up) }),
    micans:    () => ({ shape: "heart", fill: C.bronze, stroke: "#3f3a20", deco: (r, L, W, up) => decoBlobs(r, L, W, "#8a7a46", 1, up) }),

    discolor:  () => ({ shape: "heart", fill: C.velvet, stroke: C.dark, deco: (r, L, W, up) => decoBlobs(r, L, W, C.silver, 2, up) + `<path d="M0 0 L0 ${up ? -1 : 1}" stroke="none"/>` }),
    dioLeaf:   () => ({ shape: "heart", fill: C.leaf, stroke: C.forest }),
  };

  function decoVeinNet(L, W, col, up) {
    let s = "";
    const dir = up ? -1 : 1;
    for (let i = 1; i <= 5; i++) {
      const f = i / 6;
      for (const side of [-1, 1]) {
        s += `<path d="M0 ${N(dir * L * f * 0.85)} Q ${N(side * W * 0.3)} ${N(dir * L * (f * 0.85 + 0.05))} ${N(side * W * 0.5)} ${N(dir * L * (f * 0.85 - 0.08))}" fill="none" stroke="${col}" stroke-width="1" stroke-opacity="0.55"/>`;
      }
    }
    return s;
  }

  function styledLeaf(r, styleKey, opts) {
    const st = LEAF_STYLES[styleKey] ? LEAF_STYLES[styleKey]() : LEAF_STYLES.hoyaPlain();
    const up = st.shape !== "heart";
    const deco = [];
    if (st.deco) deco.push(st.deco(r, opts.L, opts.W, up));
    if (st.layer) {
      const sc = st.layer.scale;
      const inner = st.shape === "heart" ? heartLeafPath(opts.L * sc, opts.W * sc) : ovalLeafPath(opts.L * sc, opts.W * sc);
      if (st.layer.edgeOnly) {
        deco.push(`<path d="${inner}" fill="none" stroke="${st.layer.fill}" stroke-width="${N(opts.W * 0.13)}" transform="translate(0 ${N(up ? -opts.L * 0.0 : 0)})"/>`);
      } else {
        deco.push(`<path d="${inner}" fill="${st.layer.fill}" transform="translate(0 ${N(up ? -opts.L * (1 - sc) * 0.5 : opts.L * (1 - sc) * 0.4)})"/>`);
      }
    }
    return leafG(r, { ...opts, shape: st.shape, fill: st.fill, stroke: st.stroke, sw: st.sw, deco });
  }

  /* ---------- compositions ---------- */

  function compVine(r, styleKey, o = {}) {
    /* climbing vine, lower-left to upper-right — hoya default */
    const p0 = { x: 150 + r() * 40, y: 620 }, p3 = { x: 400 + r() * 60, y: 130 + r() * 40 };
    const p1 = { x: 90 + r() * 80, y: 430 + r() * 60 }, p2 = { x: 470 + r() * 80, y: 300 + r() * 60 };
    let s = `<path d="${cubicPath(p0, p1, p2, p3)}" fill="none" stroke="${C.forest}" stroke-width="2.4"/>`;
    const nL = o.leaves || 6;
    for (let i = 0; i < nL; i++) {
      const t = 0.1 + (i / (nL - 1)) * 0.78 + (r() - 0.5) * 0.04;
      const pt = cubicAt(p0, p1, p2, p3, t);
      const ang = cubicTan(p0, p1, p2, p3, t);
      const side = i % 2 ? 1 : -1;
      const L = (o.leafL || 96) * (1.12 - t * 0.35) * (0.9 + r() * 0.25);
      const W = L * (o.ratio || 0.52);
      s += styledLeaf(r, styleKey, { x: pt.x, y: pt.y, rot: ang + side * (68 + r() * 22) + 90, L, W });
    }
    if (o.umbels) {
      for (let u = 0; u < o.umbels; u++) {
        const t = 0.45 + u * 0.3 + r() * 0.08;
        const pt = cubicAt(p0, p1, p2, p3, t);
        const ux = pt.x + 40 + r() * 30, uy = pt.y + 55 + r() * 25;
        s += `<path d="M${N(pt.x)} ${N(pt.y)} Q ${N(pt.x + 25)} ${N(pt.y + 40)} ${N(ux)} ${N(uy)}" fill="none" stroke="${C.forest}" stroke-width="1.6"/>`;
        s += umbel(r, ux, uy, o.umbelScale || 1.05, o.flowerCols);
      }
    }
    return s;
  }

  function compHanging(r, styleKey, o = {}) {
    /* trailing stems from top bar — epipremnum / micans */
    let s = "";
    const stems = o.stems || 3;
    s += `<line x1="150" y1="86" x2="450" y2="86" stroke="${C.gold}" stroke-width="1.2" stroke-opacity="0.65"/>`;
    for (let k = 0; k < stems; k++) {
      const x0 = 180 + k * (240 / Math.max(stems - 1, 1)) + (r() - 0.5) * 40;
      const len = (o.len || 470) * (0.72 + r() * 0.42);
      const sway = (r() - 0.5) * 170;
      const p0 = { x: x0, y: 88 }, p3 = { x: x0 + sway, y: 88 + len };
      const p1 = { x: x0 + sway * 0.15 + (r() - 0.5) * 50, y: 88 + len * 0.36 };
      const p2 = { x: x0 + sway * 0.7 + (r() - 0.5) * 50, y: 88 + len * 0.7 };
      s += `<path d="${cubicPath(p0, p1, p2, p3)}" fill="none" stroke="${o.stem || C.forest}" stroke-width="2.1"/>`;
      const nL = o.leaves || 5;
      for (let i = 0; i < nL; i++) {
        const t = 0.14 + (i / (nL - 1)) * 0.82 + (r() - 0.5) * 0.05;
        const pt = cubicAt(p0, p1, p2, p3, t);
        const side = i % 2 ? 1 : -1;
        const L = (o.leafL || 74) * (0.82 + t * 0.5) * (0.88 + r() * 0.28);
        const W = L * (o.ratio || 0.82);
        s += styledLeaf(r, styleKey, { x: pt.x, y: pt.y, rot: side * (26 + r() * 20), L, W });
      }
    }
    return s;
  }

  function compHeroLeaf(r, styleKey, o = {}) {
    /* one dramatic center leaf — gloriosum etc. */
    let s = "";
    const cx = 300, cy = 170 + (r() - 0.5) * 20;
    s += `<path d="M${N(cx)} ${N(cy)} C ${N(cx - 30)} ${N(cy + 170)}, ${N(cx + 30)} ${N(cy + 260)}, ${N(cx + 8)} ${N(cy + 420)}" fill="none" stroke="${o.stem || C.forest}" stroke-width="3"/>`;
    s += styledLeaf(r, styleKey, { x: cx, y: cy, rot: (r() - 0.5) * 8, L: o.L || 390, W: o.W || 330 });
    if (o.companions) {
      s += styledLeaf(r, styleKey, { x: cx - 150, y: cy + 260, rot: -24 + r() * 10, L: 180, W: 150 });
      s += styledLeaf(r, styleKey, { x: cx + 160, y: cy + 240, rot: 20 + r() * 10, L: 150, W: 126 });
    }
    return s;
  }

  function compRosette(r, styleKey, o = {}) {
    /* upright cluster from a crown — birkin */
    let s = "";
    const cx = 300, cy = 560;
    const n = o.leaves || 7;
    for (let i = 0; i < n; i++) {
      const f = i / (n - 1);
      const ang = -74 + f * 148 + (r() - 0.5) * 10;
      const L = (o.leafL || 210) * (0.62 + Math.sin(f * Math.PI) * 0.5) * (0.9 + r() * 0.2);
      const W = L * 0.5;
      const bx = cx + (f - 0.5) * 60;
      s += `<path d="M${N(cx)} ${N(cy)} L ${N(bx + Math.sin((ang * Math.PI) / 180) * 30)} ${N(cy - 40)}" stroke="${C.forest}" stroke-width="2.4" fill="none"/>`;
      s += styledLeaf(r, styleKey, { x: bx + Math.sin((ang * Math.PI) / 180) * 34, y: cy - 44, rot: ang, L, W });
    }
    return s;
  }

  function tortoiseDome(r, cx, cy, w, h, cells) {
    let s = `<ellipse cx="${N(cx)}" cy="${N(cy + h - 6)}" rx="${N(w * 0.72)}" ry="10" fill="${C.caudexD}" fill-opacity="0.18"/>`;
    const domeId = uid();
    const dome = `M ${N(cx - w / 2)} ${N(cy + h)} C ${N(cx - w / 2 - 8)} ${N(cy + h * 0.25)}, ${N(cx - w * 0.3)} ${N(cy)}, ${N(cx)} ${N(cy)} C ${N(cx + w * 0.3)} ${N(cy)}, ${N(cx + w / 2 + 8)} ${N(cy + h * 0.25)}, ${N(cx + w / 2)} ${N(cy + h)} Z`;
    s += `<path d="${dome}" fill="${C.caudex}" stroke="${C.caudexD}" stroke-width="2"/>`;
    s += `<clipPath id="${domeId}"><path d="${dome}"/></clipPath><g clip-path="url(#${domeId})">`;
    const rows = cells || 3;
    for (let row = 0; row < rows; row++) {
      const ny = cy + 14 + row * (h / rows) * 0.92;
      const per = 4 + row;
      for (let i = 0; i <= per; i++) {
        const nx = cx - w / 2 + (i / per) * w + (row % 2 ? w / per / 2 : 0);
        const rad = (h / rows) * (0.52 + r() * 0.2);
        const shade = ["#cbb88c", "#d8c79e", "#e0d2ac", "#c2ae80"][Math.floor(r() * 4)];
        s += `<path d="${blobPath(r, nx, ny, rad, 6, 0.22)}" fill="${shade}" stroke="${C.caudexD}" stroke-width="1.6"/>`;
      }
    }
    s += `</g><path d="${dome}" fill="none" stroke="${C.caudexD}" stroke-width="2"/>`;
    return s;
  }

  function compCaudex(r, o = {}) {
    let s = "";
    const cx = 300, baseY = 640, h = o.h || 190, w = o.w || 290;
    const topY = baseY - h;
    /* wiry vines first (behind) */
    const vines = o.vines || 2;
    for (let v = 0; v < vines; v++) {
      const x0 = cx + (v - (vines - 1) / 2) * 46;
      let d = `M ${N(x0)} ${N(topY + 14)}`;
      let px = x0, py = topY + 14;
      const segs = 5 + Math.floor(r() * 3);
      for (let i = 0; i < segs; i++) {
        const nx = x0 + (r() - 0.5) * 190;
        const ny = py - (52 + r() * 46);
        d += ` Q ${N(px + (r() - 0.5) * 90)} ${N((py + ny) / 2)} ${N(nx)} ${N(ny)}`;
        px = nx; py = ny;
        if (py < 130) break;
      }
      s += `<path d="${d}" fill="none" stroke="${C.bronze}" stroke-width="1.7"/>`;
      /* small heart leaves along the vine */
      const nLeaf = o.leafCount || 7;
      for (let i = 0; i < nLeaf; i++) {
        const ly = topY - 10 - (i / nLeaf) * (topY - 150) - r() * 26;
        const lx = x0 + (r() - 0.5) * 180;
        s += styledLeaf(r, o.leafStyle || "dioLeaf", { x: lx, y: ly, rot: (r() - 0.5) * 70, L: (o.leafL || 30) * (0.8 + r() * 0.5), W: (o.leafL || 30) * (0.75 + r() * 0.4) });
      }
    }
    s += tortoiseDome(r, cx, topY, w, h, o.cells || 3);
    return s;
  }

  function compClimberBig(r, styleKey, o = {}) {
    /* big-leaf climber on an S-curve — leaves alternate clearly
       left/right on short petioles so the silhouette breathes */
    const p0 = { x: 285 + r() * 30, y: 635 }, p3 = { x: 300 + r() * 40, y: 105 };
    const p1 = { x: 165 + r() * 50, y: 480 }, p2 = { x: 435 + r() * 50, y: 250 };
    const stem = o.stem || C.forest;
    let s = `<path d="${cubicPath(p0, p1, p2, p3)}" fill="none" stroke="${stem}" stroke-width="2.6"/>`;
    const nL = o.leaves || 5;
    for (let i = 0; i < nL; i++) {
      const t = 0.08 + (i / (nL - 1)) * 0.84;
      const pt = cubicAt(p0, p1, p2, p3, t);
      const side = i % 2 ? 1 : -1;
      const L = (o.leafL || 104) * (0.82 + r() * 0.3) * (1.08 - t * 0.3);
      const ox = side * (L * 0.55 + 10), oy = 8 + r() * 16;
      s += `<path d="M${N(pt.x)} ${N(pt.y)} Q ${N(pt.x + ox * 0.55)} ${N(pt.y + oy * 0.1)} ${N(pt.x + ox * 0.82)} ${N(pt.y + oy * 0.5)}" fill="none" stroke="${stem}" stroke-width="2"/>`;
      s += styledLeaf(r, styleKey, { x: pt.x + ox * 0.82, y: pt.y + oy * 0.5, rot: side * (26 + r() * 16), L, W: L * 0.8 });
    }
    return s;
  }

  const GENUS_COMP = {
    hoya: (r, style) => {
      if (style === "linearis") return compHanging(r, "linearis", { stems: 4, leaves: 9, leafL: 46, ratio: 0.16, len: 500 });
      if (style === "bella") return compVine(r, "bella", { leaves: 8, leafL: 62, umbels: 2, umbelScale: 1.15, flowerCols: { outer: "#f2e2d6", inner: C.blush, dot: C.wine } });
      if (style === "kerrii") return compVine(r, "kerrii", { leaves: 5, leafL: 88, ratio: 0.95, umbels: 1 });
      return compVine(r, style, { leaves: 6, umbels: style === "callisto" ? 1 : 2, leafL: style === "callisto" ? 118 : 96 });
    },
    epipremnum: (r, style) => {
      if (style === "cebu") return compHanging(r, "cebu", { stems: 3, leaves: 6, leafL: 88, ratio: 0.42, len: 500 });
      return compHanging(r, style, { stems: 3, leaves: 5, leafL: 80, ratio: 0.84, len: 480 });
    },
    philodendron: (r, style) => {
      if (style === "birkin") return compRosette(r, "birkin", {});
      if (style === "micans") return compHanging(r, "micans", { stems: 3, leaves: 6, leafL: 64, ratio: 0.9, len: 460, stem: "#4a3f22" });
      if (style === "gloriosum") return compHeroLeaf(r, "gloriosum", { companions: true });
      if (style === "ppp") return compClimberBig(r, "ppp", { leaves: 5, leafL: 118, stem: "#3f2b1f" });
      if (style === "whiteknight") return compClimberBig(r, "whiteknight", { leaves: 5, leafL: 118, stem: C.wine });
      return compHeroLeaf(r, style, {});
    },
    dioscorea: (r, style) => {
      if (style === "discolor") return compClimberBig(r, "discolor", { leaves: 5, leafL: 128 });
      if (style === "sylvatica") return compCaudex(r, { h: 130, w: 240, cells: 2, vines: 3, leafL: 24, leafCount: 9 });
      return compCaudex(r, {});
    },
  };

  /* ---------- plate frame ---------- */

  function paperDefs(fid) {
    return (
      `<filter id="${fid}"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" result="n"/>` +
      `<feColorMatrix in="n" type="matrix" values="0 0 0 0 0.18  0 0 0 0 0.26  0 0 0 0 0.16  0 0 0 0.6 0"/></filter>`
    );
  }

  function plateFrame(w, h, caption, plateNo, bleed) {
    /* bleed: frame hugs the SVG edge so the plate reads as large as an
       edge-to-edge photo (product cards); default is the matted-print inset */
    const fi = bleed ? 2 : 20;
    const fid = uid();
    let s = `<defs>${paperDefs(fid)}</defs>`;
    s += `<rect width="${w}" height="${h}" fill="${C.paper}"/>`;
    s += `<rect width="${w}" height="${h}" filter="url(#${fid})" opacity="0.05"/>`;
    s += `<rect x="${fi}" y="${fi}" width="${w - fi * 2}" height="${h - fi * 2}" fill="none" stroke="${C.gold}" stroke-width="1" stroke-opacity="0.55"/>`;
    /* corner ticks */
    const t = 13, o2 = bleed ? 14 : 28;
    for (const [cx, cy, dx, dy] of [[o2, o2, 1, 1], [w - o2, o2, -1, 1], [o2, h - o2, 1, -1], [w - o2, h - o2, -1, -1]]) {
      s += `<path d="M${cx + dx * t} ${cy} L${cx} ${cy} L${cx} ${cy + dy * t}" fill="none" stroke="${C.gold}" stroke-width="1" stroke-opacity="0.8"/>`;
    }
    if (plateNo) {
      s += `<text x="${w / 2}" y="${h - 52}" text-anchor="middle" font-family="Karla, sans-serif" font-size="11" letter-spacing="4" fill="${C.gold}">PL. ${plateNo}</text>`;
    }
    if (caption) {
      s += `<text x="${w / 2}" y="${h - 32}" text-anchor="middle" font-family="Karla, sans-serif" font-size="12.5" letter-spacing="3.2" fill="${C.forest}" fill-opacity="0.75">${caption.toUpperCase()}</text>`;
    }
    return s;
  }

  /* ---------- public: product plate ---------- */

  function plate(product, variant = 0, seedExtra = "", opts = {}) {
    const w = 600, h = 750;
    const r = mulberry32(hashCode(product.id + ":" + variant + ":" + seedExtra));
    const style = product.artStyle;
    const genus = product.genus;
    let art = "";
    let caption = product.caption || product.name;

    if (variant === 0) {
      art = GENUS_COMP[genus](r, style);
    } else if (variant === 1) {
      /* leaf detail */
      const st = LEAF_STYLES[style] ? LEAF_STYLES[style]() : LEAF_STYLES.hoyaPlain();
      const up = st.shape !== "heart";
      art = styledLeaf(r, style, { x: 300, y: up ? 560 : 180, rot: (r() - 0.5) * 14, L: 380, W: 380 * (style === "linearis" ? 0.14 : st.shape === "heart" ? 0.88 : 0.56) });
      caption = "leaf study";
    } else {
      /* sprig / signature detail */
      if (genus === "hoya") {
        art = `<path d="M300 640 C 300 520, 290 470, 300 420" fill="none" stroke="${C.forest}" stroke-width="2.4"/>` + umbel(r, 300, 330, 2.5, style === "bella" ? { outer: "#f2e2d6", inner: C.blush, dot: C.wine } : undefined);
        caption = "bloom study";
      } else if (genus === "dioscorea") {
        art = tortoiseDome(r, 300, 250, 380, 260, 4);
        caption = "caudex study";
      } else {
        const st = LEAF_STYLES[style] ? LEAF_STYLES[style]() : LEAF_STYLES.marble();
        art = `<path d="M300 120 C 300 260, 296 380, 300 560" fill="none" stroke="${C.forest}" stroke-width="2.6"/>` +
          `<circle cx="300" cy="150" r="4.5" fill="${C.forest}"/>` +
          `<path d="M300 150 q -34 10 -40 44" fill="none" stroke="${C.caudexD}" stroke-width="1.6"/>` +
          styledLeaf(r, style, { x: 300, y: 190, rot: 12, L: 240, W: 210 });
        caption = "cutting study";
      }
    }
    /* seeded mirror of the artwork (never the frame text) — with many
       same-style plants in one grid the compositions read as clones otherwise */
    const flip = mulberry32(hashCode(product.id + ":flip"))() < 0.5;
    const artTransform = flip ? ` transform="translate(${w} 0) scale(-1 1)"` : "";
    return `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Botanical illustration of ${product.name}">${plateFrame(w, h, caption, product.plateNo, opts.bleed)}<g${artTransform}>${art}</g></svg>`;
  }

  /* ---------- medallions ---------- */

  function medallion(genusKey, theme = "light") {
    const r = mulberry32(hashCode("medallion:" + genusKey));
    const ring = theme === "dark" ? C.goldL : C.gold;
    const stroke = theme === "dark" ? C.goldL : C.forest;
    let motif = "";
    if (genusKey === "hoya") {
      motif = `<path d="M160 208 C 150 190, 148 176, 160 156" fill="none" stroke="${stroke}" stroke-width="1.8"/>` + umbel(r, 160, 128, 1.15);
      motif += styledLeaf(r, "hoyaPlain", { x: 148, y: 212, rot: -118, L: 52, W: 27 });
      motif += styledLeaf(r, "hoyaPlain", { x: 174, y: 210, rot: 116, L: 52, W: 27 });
    } else if (genusKey === "epipremnum") {
      motif = styledLeaf(r, "marble", { x: 160, y: 96, rot: 3, L: 92, W: 84 });
    } else if (genusKey === "philodendron") {
      motif = styledLeaf(r, "gloriosum", { x: 160, y: 92, rot: -2, L: 96, W: 86 });
    } else {
      motif = tortoiseDome(r, 160, 150, 120, 74, 2) + `<path d="M160 152 Q 140 120 156 96 Q 170 76 152 58" fill="none" stroke="${C.bronze}" stroke-width="1.5"/>` + styledLeaf(r, "dioLeaf", { x: 150, y: 62, rot: -30, L: 20, W: 17 });
    }
    return `<svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" class="medallion" role="img" aria-label="${genusKey} emblem"><circle cx="160" cy="160" r="128" fill="none" stroke="${ring}" stroke-width="1.4" stroke-opacity="0.85"/><circle cx="160" cy="160" r="121" fill="none" stroke="${ring}" stroke-width="0.6" stroke-opacity="0.4"/>${motif}</svg>`;
  }

  /* ---------- hero art ---------- */

  function hero(theme = "dark") {
    const r = mulberry32(hashCode("hero-art-v1"));
    const light = theme === "light";
    const ring = light ? C.gold : C.goldL;          /* the logo-echo circle */
    const line = light ? "#33573a" : C.goldL;       /* foliage linework */
    const petal = light ? "rgba(232,169,173,0.4)" : "rgba(232,169,173,0.16)";
    const inner = light ? "rgba(241,246,193,0.65)" : "rgba(241,246,193,0.2)";
    const leafFill = light ? "rgba(232,169,173,0.12)" : "rgba(232,169,173,0.07)";
    const fleck = light ? "rgba(181,146,75,0.55)" : "rgba(212,179,106,0.5)";
    const dot = light ? C.wine : C.goldL;
    let s = `<svg viewBox="0 0 720 660" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Botanical line art with gold ring">`;
    s += `<style>` +
      `.sway{animation:tfsway 7s ease-in-out infinite alternate;transform-origin:center}` +
      `@keyframes tfsway{from{transform:rotate(-2.1deg) translateY(4px)}to{transform:rotate(2.4deg) translateY(-6px)}}` +
      `.flut{animation:tfflut 5s ease-in-out infinite alternate;transform-origin:0 0}` +
      `@keyframes tfflut{from{transform:rotate(-2.8deg)}to{transform:rotate(3.2deg)}}` +
      `.tw{animation:tftw 4s ease-in-out infinite alternate}` +
      `@keyframes tftw{from{opacity:.2}to{opacity:1}}` +
      /* entrance: the vine draws itself, leaves unfurl as it passes */
      `.vinedraw{stroke-dasharray:1;stroke-dashoffset:1;animation:tfdraw 2.3s ease-out forwards}` +
      `@keyframes tfdraw{to{stroke-dashoffset:0}}` +
      `.grow{animation:tfgrowin 0.9s cubic-bezier(.34,1.45,.5,1) both;transform-origin:0 0}` +
      `@keyframes tfgrowin{from{transform:scale(0)}to{transform:scale(1)}}` +
      /* a bloom-pulse travels around the umbel */
      `.bloom{animation:tfbloom 11s ease-in-out infinite;transform-origin:0 0}` +
      `@keyframes tfbloom{0%,74%,100%{transform:scale(1)}82%{transform:scale(1.28)}90%{transform:scale(.94)}96%{transform:scale(1.06)}}` +
      /* stray petals drift down from the umbel */
      `.petal{opacity:0;transform-origin:0 0}` +
      `.petal-a{animation:tfpetala var(--pd,13s) linear var(--pw,2s) infinite}` +
      `@keyframes tfpetala{0%{transform:translate(0,0) rotate(0deg);opacity:0}5%{opacity:.9}35%{transform:translate(-70px,160px) rotate(80deg);opacity:.85}70%{transform:translate(-150px,340px) rotate(190deg);opacity:.6}92%{opacity:0}100%{transform:translate(-190px,470px) rotate(280deg);opacity:0}}` +
      `.petal-b{animation:tfpetalb var(--pd,15s) linear var(--pw,7s) infinite}` +
      `@keyframes tfpetalb{0%{transform:translate(0,0) rotate(0deg);opacity:0}5%{opacity:.85}30%{transform:translate(40px,150px) rotate(-70deg);opacity:.8}65%{transform:translate(-30px,330px) rotate(-180deg);opacity:.55}90%{opacity:0}100%{transform:translate(10px,460px) rotate(-260deg);opacity:0}}` +
      `@media(prefers-reduced-motion:reduce){.sway,.flut,.tw,.grow,.bloom{animation:none}.vinedraw{animation:none;stroke-dasharray:none}.petal{display:none}}` +
      `</style>`;
    /* gold ring, echoing the logo */
    s += `<circle cx="380" cy="322" r="248" fill="none" stroke="${ring}" stroke-width="1.3" stroke-opacity="0.9"/>`;
    s += `<circle cx="380" cy="322" r="238" fill="none" stroke="${ring}" stroke-width="0.5" stroke-opacity="0.35"/>`;
    /* trailing pothos vine sweeping through the ring — draws itself in */
    const p0 = { x: 95, y: 548 }, p1 = { x: 260, y: 545 }, p2 = { x: 215, y: 370 }, p3 = { x: 470, y: 245 };
    s += `<g class="sway"><path class="vinedraw" pathLength="1" d="${cubicPath(p0, p1, p2, p3)}" fill="none" stroke="${line}" stroke-width="1.6" stroke-opacity="0.9"/>`;
    for (let i = 0; i < 6; i++) {
      const t = 0.12 + i * 0.15;
      const pt = cubicAt(p0, p1, p2, p3, t);
      const ang = cubicTan(p0, p1, p2, p3, t);
      const side = i % 2 ? 1 : -1;
      const L = 76 * (1.05 - t * 0.3);
      const cid = uid();
      const pd = heartLeafPath(L, L * 0.85);
      s += `<g transform="translate(${N(pt.x)} ${N(pt.y)}) rotate(${N(ang + side * 74 + 92)})">` +
        `<g class="grow" style="animation-delay:${N(0.15 + t * 2.05)}s">` +
        `<g class="flut" style="animation-duration:${N(4.2 + i * 0.55)}s;animation-delay:${N(-i * 0.9)}s">` +
        `<path d="${pd}" fill="${leafFill}" stroke="${line}" stroke-width="1.5"/>` +
        `<clipPath id="${cid}"><path d="${pd}"/></clipPath>` +
        `<g clip-path="url(#${cid})">${decoFlecks(r, L, L * 0.85, fleck, 10, false)}</g>` +
        midribHeart(L, L * 0.85, line) + `</g></g></g>`;
    }
    s += `</g>`;
    /* hoya umbel upper right, budding off the vine tip.
       Pops open after the vine reaches it; a bloom-pulse then travels
       around the flowers; petals occasionally drift down. */
    s += `<g class="sway" style="animation-delay:-4s">`;
    s += `<path class="vinedraw" pathLength="1" style="animation-delay:2s;animation-duration:0.7s" d="M470 245 C 500 220, 520 200, 542 178" fill="none" stroke="${line}" stroke-width="1.4"/>`;
    const ucx = 556, ucy = 164;
    s += `<g transform="translate(${ucx} ${ucy})"><g class="grow" style="animation-delay:2.5s">`;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const fx = Math.cos(a) * 52, fy = Math.sin(a) * 48;
      s += `<line x1="0" y1="0" x2="${N(fx)}" y2="${N(fy)}" stroke="${line}" stroke-width="0.9" stroke-opacity="0.55"/>`;
      s += `<g transform="translate(${N(fx)} ${N(fy)})"><g class="bloom" style="animation-delay:${N(i * 0.55)}s">` +
        `<path d="${starPath(0, 0, 17, 8.4, r() * 1.3)}" fill="${petal}" stroke="${line}" stroke-width="1.2"/>` +
        `<path d="${starPath(0, 0, 8, 3.8, r() * 1.3 + 0.63)}" fill="${inner}" stroke="${line}" stroke-width="0.8"/>` +
        `<circle cx="0" cy="0" r="2" fill="${dot}"/>` +
        `</g></g>`;
    }
    s += `<g class="bloom" style="animation-delay:${N(10 * 0.55)}s"><path d="${starPath(0, 0, 15, 7.4, 0.4)}" fill="${petal}" stroke="${line}" stroke-width="1.2"/></g>`;
    s += `</g></g>`;
    /* falling petals, released from just under the umbel */
    const petalShape = `M0 0 C 7 3, 9 12, 0 22 C -9 12, -7 3, 0 0 Z`;
    const petalFill = light ? "rgba(232,169,173,0.75)" : "rgba(232,169,173,0.5)";
    for (const [px, py, cls, dur, wait] of [[540, 215, "petal-a", 13, 3], [575, 205, "petal-b", 16, 9], [520, 210, "petal-a", 18, 14]]) {
      s += `<g transform="translate(${px} ${py})"><path class="petal ${cls}" style="--pd:${dur}s;--pw:${wait}s" d="${petalShape}" fill="${petalFill}" stroke="${line}" stroke-width="0.8" stroke-opacity="0.5"/></g>`;
    }
    s += `</g>`;
    /* scattered sparkles, gently twinkling */
    for (let i = 0; i < 7; i++) {
      const x = 90 + r() * 560, y = 60 + r() * 520, sc = 3 + r() * 5;
      s += `<path class="tw" style="animation-duration:${N(3.2 + r() * 2.6)}s;animation-delay:${N(-r() * 4)}s" d="M${N(x)} ${N(y - sc)} Q ${N(x + sc * 0.18)} ${N(y - sc * 0.18)} ${N(x + sc)} ${N(y)} Q ${N(x + sc * 0.18)} ${N(y + sc * 0.18)} ${N(x)} ${N(y + sc)} Q ${N(x - sc * 0.18)} ${N(y + sc * 0.18)} ${N(x - sc)} ${N(y)} Q ${N(x - sc * 0.18)} ${N(y - sc * 0.18)} ${N(x)} ${N(y - sc)} Z" fill="${ring}" fill-opacity="${N(0.5 + r() * 0.4)}"/>`;
    }
    s += `</svg>`;
    return s;
  }

  /* ---------- small icons ---------- */

  function icon(name) {
    const st = `fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;
    const wrap = (inner) => `<svg viewBox="0 0 48 48" class="v-icon" aria-hidden="true">${inner}</svg>`;
    if (name === "sprout")
      return wrap(`<circle cx="24" cy="24" r="21" ${st} stroke-opacity="0.45"/><path d="M24 34 V 20 M24 20 C 24 12, 30 8, 37 9 C 36 16, 31 20, 24 20 M24 26 C 24 20, 18 17, 12 18 C 13 24, 18 26, 24 26" ${st}/>`);
    if (name === "parcel")
      return wrap(`<path d="M9 17 L24 10 L39 17 L39 33 L24 40 L9 33 Z M9 17 L24 24 L39 17 M24 24 V 40" ${st}/><path d="M17 13.5 L31 20.5" ${st} stroke-opacity="0.7"/>`);
    if (name === "heartleaf")
      return wrap(`<path d="M24 40 C 12 30, 8 22, 12 14 C 16 8, 23 9, 24 15 C 25 9, 32 8, 36 14 C 40 22, 36 30, 24 40 Z" ${st}/><path d="M15 26 h6 l2.5 -5 3 9 2.5 -4 h6" ${st} stroke-opacity="0.85"/>`);
    if (name === "shield")
      return wrap(`<path d="M24 8 L37 13 V 24 C 37 32, 31 38, 24 41 C 17 38, 11 32, 11 24 V 13 Z" ${st}/><path d="M18 24 l4.5 4.5 L31 20" ${st}/>`);
    if (name === "gavel")
      return wrap(`<path d="M20 14 L30 24 M25 9 L35 19 M22 12 L32 22 M18 26 L28 16" ${st}/><path d="M23 21 L10 34 a3 3 0 0 0 4 4 L27 25" ${st}/><path d="M30 40 h10" ${st}/>`);
    return "";
  }

  function ornament() {
    return `<svg viewBox="0 0 120 18" aria-hidden="true"><path d="M4 9 H46" stroke="currentColor" stroke-width="1" stroke-opacity="0.5"/><path d="M74 9 H116" stroke="currentColor" stroke-width="1" stroke-opacity="0.5"/><path d="M60 2 Q 63 6 60 9 Q 57 6 60 2 M60 16 Q 63 12 60 9 Q 57 12 60 16" fill="currentColor"/><circle cx="52" cy="9" r="1.4" fill="currentColor"/><circle cx="68" cy="9" r="1.4" fill="currentColor"/></svg>`;
  }

  return { plate, medallion, hero, icon, ornament, hashCode, mulberry32 };
})();
